import { centralDbRequest } from './centralDbClient';

export type AiObservabilityQuery = {
  from?: string;
  to?: string;
  provider?: string;
  model?: string;
  requestPath?: string;
  taskType?: string;
  success?: boolean;
  fallback?: boolean;
  search?: string;
  page?: number;
  pageSize?: number;
  sortBy?: 'startedAt' | 'durationMs' | 'estimatedCostUsd';
  sortDir?: 'asc' | 'desc';
};

export type AiObservabilityEvent = {
  id: string;
  sourceService: string | null;
  requestPath: string;
  taskType: string | null;
  provider: string | null;
  model: string | null;
  processedBy: string | null;
  isSuccess: boolean;
  isFallback: boolean;
  statusCode: number | null;
  durationMs: number | null;
  inputChars: number | null;
  outputChars: number | null;
  inputTokens: number | null;
  outputTokens: number | null;
  totalTokens: number | null;
  tokensEstimated: boolean;
  estimatedCostUsd: number | null;
  errorMessage: string | null;
  requestId: string | null;
  workerId: string | null;
  sessionId: string | null;
  language: string | null;
  inputPreview: string | null;
  outputPreview: string | null;
  requestPayload?: Record<string, any> | null;
  responsePayload?: Record<string, any> | null;
  metadata?: Record<string, any> | null;
  startedAt: string;
  endedAt: string | null;
  createdAt: string;
};

export type AiObservabilityListResponse = {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  items: AiObservabilityEvent[];
};

export type AiObservabilityStatsResponse = {
  totals: {
    totalCalls: number;
    successCalls: number;
    fallbackCalls: number;
    failedCalls: number;
    successRatePct: number;
    fallbackRatePct: number;
    avgDurationMs: number;
    p95DurationMs: number;
    totalInputChars: number;
    totalOutputChars: number;
    totalInputTokens: number;
    totalOutputTokens: number;
    totalTokens: number;
    totalEstimatedCostUsd: number;
  };
  byProviderModel: Array<{
    provider: string;
    model: string;
    calls: number;
    successCalls: number;
    fallbackCalls: number;
    avgDurationMs: number;
    estimatedCostUsd: number;
  }>;
  byEndpoint: Array<{
    requestPath: string;
    calls: number;
    successCalls: number;
    fallbackCalls: number;
    avgDurationMs: number;
    estimatedCostUsd: number;
  }>;
  byTaskType: Array<{
    taskType: string;
    calls: number;
    successCalls: number;
    fallbackCalls: number;
    avgDurationMs: number;
    estimatedCostUsd: number;
  }>;
  timeline: Array<{
    day: string;
    calls: number;
    successCalls: number;
    fallbackCalls: number;
    avgDurationMs: number;
    estimatedCostUsd: number;
  }>;
};

const toQuery = (query: AiObservabilityQuery): Record<string, string> => {
  const out: Record<string, string> = {};
  const entries = Object.entries(query) as Array<[keyof AiObservabilityQuery, any]>;
  for (const [key, value] of entries) {
    if (value === undefined || value === null || value === '') continue;
    out[key] = String(value);
  }
  return out;
};

async function get<T>(path: string, query: AiObservabilityQuery, token?: string | null): Promise<T> {
  const res = await centralDbRequest({ method: 'GET', path, query: toQuery(query), token });
  if (!res.ok) throw new Error(`AI Observability API error: ${res.status}`);
  return res.data as T;
}

export const aiObservabilityApi = {
  listEvents: (query: AiObservabilityQuery, token?: string | null) =>
    get<AiObservabilityListResponse>('/admin/ai-observability/events', query, token),

  getStats: (query: AiObservabilityQuery, token?: string | null) =>
    get<AiObservabilityStatsResponse>('/admin/ai-observability/stats', query, token),

  getEventById: async (id: string, token?: string | null) => {
    const res = await centralDbRequest({
      method: 'GET',
      path: `/admin/ai-observability/events/${id}`,
      token,
    });
    if (!res.ok) throw new Error(`AI Observability API error: ${res.status}`);
    return res.data as AiObservabilityEvent;
  },
};

